// src/components/Experiment/DataAnalysisStage.js
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Alert, Spinner, Button } from 'react-bootstrap';
import DataTable from './DataTable';
import AnalysisToolsContainer from './AnalysisToolsContainer';
import AnalysisDialog from './AnalysisDialog';
import DataGenerationDialog from './DataGenerationDialog';
import AdjustmentsDialog from './AdjustmentsDialog';
import ImportExcelDialog  from './ImportExcelDialog';
import ExportSuggestionDialog from './ExportSuggestionDialog';
import {
  getExperimentData,
  updateExperimentData,
  createAnalysis,
  getAnalysis,
  createVisualization,
  exportExperimentData,
  generateExperimentData,
  getExperimentFile,
  downloadFile,
  createAdjustmentsTable,
  updateAdjustmentsTable,
  deleteAdjustmentsTable,
  getAdjustmentsTables,
  uploadExcelAdjustments,
  applyAdjustmentsToExperiment,
  getExperimentAdjustmentsStatus
} from '../../api/photoProcessingApi';
import './DataAnalysisStage.css';

const DataAnalysisStage = ({ experiment, onExperimentUpdate, hasShownExportDialogRef }) => {
  const [experimentData, setExperimentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [showGenerationDialog, setShowGenerationDialog] = useState(false);
  const [showAdjustmentsDialog, setShowAdjustmentsDialog] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [exportStatus, setExportStatus] = useState('idle');
  const [adjustmentsStatus, setAdjustmentsStatus] = useState('idle');

  // Adjustments state
  const [adjustmentsTables, setAdjustmentsTables] = useState([]);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const [adjustmentsError, setAdjustmentsError] = useState(null);
  const [adjustmentsSuccess, setAdjustmentsSuccess] = useState(null);

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const [isRangeSelectionMode, setIsRangeSelectionMode] = useState(false);
  const [rangeSelectionCallback, setRangeSelectionCallback] = useState(null);

  // Load experiment data table
  useEffect(() => {
    if (experiment.id) {
      checkExperimentData();
    }
  }, [experiment.id]);

  // Update local state when experiment data changes from parent
  useEffect(() => {
    if (experiment.experimentData) {
      setExperimentData(experiment.experimentData);
    }
  }, [experiment.experimentData]);

  const checkExperimentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Always try to get data, even if experiment.experimentData is null
      const data = await getExperimentData(experiment.id);

      if (data.status === 'completed') {
        setGenerationStatus('idle');
        setExperimentData(data);
        // Update parent with new data
        if (onExperimentUpdate) {
          console.log("Experiment update");
          onExperimentUpdate({experimentData: data});
        }
        // Only show export suggestion on first load
        if (!hasShownExportDialogRef.current) {
          hasShownExportDialogRef.current = true;
          setTimeout(() => {
            setShowExportDialog(true);
          }, 500);
        }

      } else if (data.status === 'processing') {
        // Data is being generated or imported, poll until ready
        pollForData();

      } else if (data.status === 'failed' && experimentData) {
        // A generate/import attempt failed, but we already have a good table
        // displayed - keep showing it instead of replacing it with "No Data Available"
        setDataError('Data operation failed: ' + (data.error_message || 'Unknown error'));

      } else {
        // No data or failed with nothing previously loaded
        setExperimentData(null);

        // Don't automatically show generation dialog
        // Let user choose between Generate and Import
        // setShowGenerationDialog(true); // Commented out
      }

    } catch (error) {
      console.error('Error checking experiment data:', error);

      // Handle different error types
      if (error.response?.status === 404) {
        // No data exists yet - this is normal, don't show as error
        setExperimentData(null);
        setError(null); // Clear any previous errors

      } else if (error.message.includes('No data available')) {
        // No data exists yet
        setExperimentData(null);
        setError(null);

      } else {
        // Real error
        setError('Failed to load experiment data: ' + error.message);
      }
    } finally {
      console.log("Set loading");
      setLoading(false);
    }
  };

  const pollForData = async () => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      try {
        const data = await getExperimentData(experiment.id);

        if (data.status === 'completed') {
          setExperimentData(data);
          setGenerationStatus('idle');

          if (!hasShownExportDialogRef.current) {
            hasShownExportDialogRef.current = true;
            setTimeout(() => {
              setShowExportDialog(true);
            }, 500);
          }

          // Update parent with new data
          if (onExperimentUpdate) {
            console.log("Experiment update poll");
            onExperimentUpdate({experimentData: data});
          }
          return;
        } else if (data.status === 'failed') {
          const message = 'Data operation failed: ' + (data.error_message || 'Unknown error');
          if (experimentData) {
            // Keep the last good table visible instead of hiding it behind a full-page error
            setDataError(message);
            setGenerationStatus('idle');
          } else {
            setError(message);
            setGenerationStatus('error');
            setShowGenerationDialog(true);
          }
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setError('Data generation timed out');
          setGenerationStatus('error');
          setShowGenerationDialog(true);
        }
      } catch (error) {
        console.error('Error polling for data:', error);
        setError('Failed to poll for data: ' + error.message);
        setGenerationStatus('error');
      }
    };

    poll();
  };

  // Poll for adjustments completion
  const pollForAdjustmentsCompletion = async () => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      try {
        const status = await getExperimentAdjustmentsStatus(experiment.id);

        if (status.status === 'completed') {
          setAdjustmentsStatus('completed');
          setAdjustmentsSuccess('Adjustments applied successfully!');

          // Refresh experiment data
          await checkExperimentData();

          // Clear success message after 3 seconds
          setTimeout(() => {
            setAdjustmentsSuccess(null);
            setAdjustmentsStatus('idle');
          }, 3000);
          return;
        } else if (status.status === 'failed') {
          setAdjustmentsStatus('error');
          setAdjustmentsError('Adjustments failed: ' + (status.error_message || 'Unknown error'));

          // Clear error after 5 seconds
          setTimeout(() => {
            setAdjustmentsError(null);
            setAdjustmentsStatus('idle');
          }, 5000);
          return;
        } else if (status.status === 'processing') {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            setAdjustmentsStatus('error');
            setAdjustmentsError('Adjustments timed out');

            setTimeout(() => {
              setAdjustmentsError(null);
              setAdjustmentsStatus('idle');
            }, 5000);
          }
        }
      } catch (error) {
        console.error('Error polling for adjustments status:', error);
        setAdjustmentsStatus('error');
        setAdjustmentsError('Failed to check adjustments status');

        setTimeout(() => {
          setAdjustmentsError(null);
          setAdjustmentsStatus('idle');
        }, 5000);
      }
    };

    poll();
  };

  // Load adjustments tables
  const loadAdjustmentsTables = async () => {
    try {
      setAdjustmentsLoading(true);
      setAdjustmentsError(null);

      const response = await getAdjustmentsTables();
      setAdjustmentsTables(response.results || []);
    } catch (error) {
      console.error('Error loading adjustments tables:', error);
      setAdjustmentsError('Failed to load adjustments tables');
    } finally {
      setAdjustmentsLoading(false);
    }
  };


  // Handle creating adjustments table
  const handleCreateAdjustmentsTable = async (tableData) => {
    try {
      setAdjustmentsLoading(true);
      setAdjustmentsError(null);

      const result = await createAdjustmentsTable(tableData);

      if (result.id) {
        setAdjustmentsSuccess('Adjustments table saved successfully');

        // Refresh the tables list
        await loadAdjustmentsTables();

        // Clear success message after 3 seconds
        setTimeout(() => {
          setAdjustmentsSuccess(null);
        }, 3000);

        return result;
      }
    } catch (error) {
      console.error('Error saving adjustments table:', error);
      setAdjustmentsError(error.message || 'Failed to save adjustments table');
      throw error;
    } finally {
      setAdjustmentsLoading(false);
    }
  };
  const handleUpdateAdjustmentsTable = async (tableId, tableData) => {
    try {
      setAdjustmentsLoading(true);
      setAdjustmentsError(null);

      const result = await updateAdjustmentsTable(tableId, tableData);

      if (result.id) {
        setAdjustmentsSuccess('Adjustments table updated successfully');

        // Refresh the tables list
        await loadAdjustmentsTables();

        // Clear success message after 3 seconds
        setTimeout(() => {
          setAdjustmentsSuccess(null);
        }, 3000);

        return result;
      }
    } catch (error) {
      console.error('Error updating adjustments table:', error);
      setAdjustmentsError(error.message || 'Failed to update adjustments table');
      throw error;
    } finally {
      setAdjustmentsLoading(false);
    }
  };
  // In DataAnalysisStage.js, add this function:

  const handleDeleteAdjustmentsTable = async (tableId) => {
    try {
      setAdjustmentsLoading(true);
      setAdjustmentsError(null);

      await deleteAdjustmentsTable(tableId);

      setAdjustmentsSuccess('Adjustments table deleted successfully');

      // Refresh the tables list
      await loadAdjustmentsTables();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setAdjustmentsSuccess(null);
      }, 3000);

    } catch (error) {
      console.error('Error deleting adjustments table:', error);
      setAdjustmentsError(error.message || 'Failed to delete adjustments table');
      throw error;
    } finally {
      setAdjustmentsLoading(false);
    }
  };

  const pollForExcelUploadCompletion = async (tableId) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      try {
        // Get the specific table status
        const response = await getAdjustmentsTables();
        const table = response.results?.find(t => t.id === tableId);

        if (!table) {
          throw new Error('Adjustments table not found');
        }

        if (table.status === 'completed') {
          setAdjustmentsSuccess('Excel file processed successfully');

          // Refresh the tables list
          await loadAdjustmentsTables();

          // Return the completed table data
          return table;

        } else if (table.status === 'failed') {
          throw new Error(table.error_message || 'Excel processing failed');

        } else if (table.status === 'running') {
          // Still processing, continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            throw new Error('Excel upload timed out');
          }

        } else {
          // PENDING or other status, continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            throw new Error('Excel upload timed out');
          }
        }
      } catch (error) {
        console.error('Error polling for Excel upload status:', error);
        throw error;
      }
    };

    return poll();
  };

  // Handle uploading Excel adjustments
  const handleUploadExcelAdjustments = async (formData) => {
    try {
      setAdjustmentsLoading(true);
      setAdjustmentsError(null);

      const result = await uploadExcelAdjustments(formData);

      if (result.success && result.table) {
        // Store the table ID for polling
        const tableId = result.table.id;

        // Start polling for completion
        const completedTable = await pollForExcelUploadCompletion(tableId);

        setAdjustmentsSuccess('Adjustments table created from Excel successfully');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setAdjustmentsSuccess(null);
        }, 3000);

        return completedTable;
      }

      throw new Error('Failed to start Excel upload');
    } catch (error) {
      console.error('Error uploading Excel adjustments:', error);
      setAdjustmentsError(error.message || 'Failed to upload Excel file');
      throw error;
    } finally {
      setAdjustmentsLoading(false);
    }
  };

  // Handle applying adjustments
  const handleApplyAdjustments = async (adjustmentsTableId, selectedColumns) => {
    try {
      setAdjustmentsStatus('applying');
      setAdjustmentsError(null);

      const result = await applyAdjustmentsToExperiment(experiment.id, {
        adjustments_table_id: adjustmentsTableId,
        selected_columns: selectedColumns
      });

      if (result.success) {
        // Start polling for completion
        pollForAdjustmentsCompletion();

        return result;
      }
    } catch (error) {
      console.error('Error applying adjustments:', error);
      setAdjustmentsStatus('error');
      setAdjustmentsError(error.message || 'Failed to apply adjustments');

      // Clear error after 5 seconds
      setTimeout(() => {
        setAdjustmentsError(null);
        setAdjustmentsStatus('idle');
      }, 5000);

      throw error;
    }
  };

  const handleGenerateData = async (generationMode, selectedJobs) => {
    try {
      setGenerationStatus('generating');
      setShowGenerationDialog(false);
      setError(null);

      console.log('Generating data with:', {generationMode, selectedJobs});

      // Generate experiment data
      const result = await generateExperimentData(experiment.id, {
        generation_mode: generationMode,
        selected_jobs: selectedJobs
      });

      console.log('Generation started:', result);

      // Start polling for the generated data
      await checkExperimentData();

    } catch (error) {
      console.error('Error generating data:', error);

      const message = 'Failed to generate data: ' + error.message;
      if (experimentData) {
        // Keep the last good table visible instead of hiding it behind a full-page error
        setDataError(message);
        setGenerationStatus('idle');
      } else {
        setError(message);
        setGenerationStatus('error');
        setShowGenerationDialog(true);
      }
    }
  };

  const handleImportSuccess = async () => {
    // Refresh the experiment data after successful import
    await checkExperimentData();
    // Show success message
    setAdjustmentsSuccess('Excel data imported successfully!');
  };

  const handleExperimentUpdate = (updates) => {
    // Update parent experiment state
    if (onExperimentUpdate) {
      onExperimentUpdate(updates);
    }
  };

  const handleCellUpdate = async (rowIndex, columnName, newValue) => {
    if (!experimentData) return;

    try {
      // Update local state
      const updatedMatrix = [...experimentData.data_matrix];
      updatedMatrix[rowIndex] = {
        ...updatedMatrix[rowIndex],
        [columnName]: newValue
      };

      const updatedData = {
        ...experimentData,
        data_matrix: updatedMatrix
      };

      setExperimentData(updatedData);

      // Update parent
      if (onExperimentUpdate) {
        onExperimentUpdate({experimentData: updatedData});
      }

      console.log('Cell updated successfully, new value:', newValue);

    } catch (error) {
      console.error('Error updating cell:', error);
      setError('Failed to update cell: ' + error.message);

      // Revert local state on error
      await checkExperimentData();
    }
  };

  const handleCellsUpdate = async (selectedRange, newValues) => {
    if (!experimentData || !selectedRange || !newValues) return;

    try {
      // Update local state
      const updatedMatrix = [...experimentData.data_matrix];

      // Apply updates to the range of cells
      for (let rowOffset = 0; rowOffset < newValues.length; rowOffset++) {
        const targetRow = selectedRange.startRow + rowOffset;

        if (targetRow >= updatedMatrix.length) break;

        for (let colOffset = 0; colOffset < newValues[rowOffset].length; colOffset++) {
          if (colOffset >= selectedRange.columnNames.length) break;

          const columnName = selectedRange.columnNames[colOffset];
          const newValue = newValues[rowOffset][colOffset];

          updatedMatrix[targetRow] = {
            ...updatedMatrix[targetRow],
            [columnName]: newValue
          };
        }
      }

      const updatedData = {
        ...experimentData,
        data_matrix: updatedMatrix
      };

      setExperimentData(updatedData);

      // Update parent
      if (onExperimentUpdate) {
        onExperimentUpdate({experimentData: updatedData});
      }

      console.log('Cells updated successfully');

    } catch (error) {
      console.error('Error updating cells:', error);
      setError('Failed to update cells: ' + error.message);

      // Revert local state on error
      checkExperimentData();
    }
  };

  // Handle range selection for analysis dialog
  const handleRangeSelection = (selectedRange) => {
    if (isRangeSelectionMode && rangeSelectionCallback) {
      rangeSelectionCallback(selectedRange);
      setIsRangeSelectionMode(false);
      setRangeSelectionCallback(null);
      setShowAnalysisDialog(true); // Show the dialog back
    }
  };

  const startRangeSelection = (callback) => {
    setRangeSelectionCallback(() => callback);
    setIsRangeSelectionMode(true);
    setShowAnalysisDialog(false); // Hide the dialog temporarily
  };

  const cancelRangeSelection = () => {
    setIsRangeSelectionMode(false);
    setRangeSelectionCallback(null);
    setShowAnalysisDialog(true); // Show the dialog back
  };

  const handleAnalysisStart = async (analysisType) => {
    if (analysisType === 'create_adjustments') {
      setShowAdjustmentsDialog(true);
      return;
    }

    if (analysisType === 'apply_adjustments') {
      // This is handled by the AdjustmentsDialog now
      return;
    }

    try {
      // Update experiment data before starting analysis
      if (experiment.experimentData) {
        const experimentDataToUpdate = {
          columns_metadata: experiment.experimentData?.columns_metadata,
          rows_metadata: experiment.experimentData?.rows_metadata,
          data_matrix: experiment.experimentData?.data_matrix
        };

        await updateExperimentData(experiment.id, experimentDataToUpdate);
      }

      // Continue with existing actions
      setSelectedAnalysisType(analysisType);
      setShowAnalysisDialog(true);

    } catch (error) {
      console.error('Failed to update experiment data before analysis:', error);
      // You might want to show an error message to the user here
      // For example: setError('Failed to save experiment data before analysis');

      // Still open the analysis dialog even if update fails?
      // Or you might want to prevent analysis from starting
      setSelectedAnalysisType(analysisType);
      setShowAnalysisDialog(true);

      // Alternatively, if you want to prevent analysis on failure:
      // alert('Failed to save experiment data. Please try again.');
      // return; // Don't open analysis dialog
    }
  };

  const handleAnalysisSubmit = async (analysisConfig) => {
    try {
      const response = await createAnalysis(experiment.id, {
        ...analysisConfig,
        analysis_type: selectedAnalysisType
      });

      // Handle response - could be single analysis object or array for 'all' type
      let analysesToAdd = [];

      if (Array.isArray(response.data)) {
        // 'all' analysis type - response contains array of analyses
        analysesToAdd = response.data;
      } else {
        // Single analysis
        analysesToAdd = [response.data];
      }

      // Add all analyses to state
      setAnalyses(prev => [...prev, ...analysesToAdd]);
      setShowAnalysisDialog(false);

      // No need to manually create score plots - backend handles it automatically
      // The backend will auto-generate score plots for pca/lda/plsda analyses

    } catch (error) {
      console.error('Error creating analysis:', error);
      setError('Failed to start analysis: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleExportExcel = async () => {
    try {
      setExportStatus('exporting');
      setShowExportDialog(false);

      const result = await exportExperimentData(experiment.id, 'excel');

      if (result.task_id) {
        // The export task may wait in the celery queue ('pending') while the server is busy:
        // wait for it up to 10 min, and up to 5 min once it has started ('running').
        const QUEUE_TIMEOUT_MS = 10 * 60 * 1000;
        const RUN_TIMEOUT_MS = 5 * 60 * 1000;
        const startedAt = Date.now();
        let runningSince = null;

        const pollForExportFile = async () => {
          try {
            const exportFile = await getExperimentFile(result.file_id);

            if (exportFile?.id) {

              if (exportFile.status === 'completed') {
                await downloadFile(exportFile.id);
                setExportStatus('completed');
                setTimeout(() => setExportStatus('idle'), 2000);
                return;
              } else if (exportFile.status === 'failed') {
                throw new Error(exportFile.error_message || 'Export failed');
              } else if (exportFile.status === 'pending' || exportFile.status === 'running') {
                const now = Date.now();
                if (exportFile.status === 'running') {
                  runningSince = runningSince || now;
                  setExportStatus('exporting');
                } else {
                  setExportStatus('queued');
                }
                if (runningSince ? now - runningSince > RUN_TIMEOUT_MS : now - startedAt > QUEUE_TIMEOUT_MS) {
                  throw new Error(runningSince
                    ? 'Export task timed out'
                    : 'Export task is still waiting in the queue - the server is busy, try again later');
                }
                setTimeout(pollForExportFile, 2000);
              }
            } else {
              if (Date.now() - startedAt < QUEUE_TIMEOUT_MS) {
                setTimeout(pollForExportFile, 2000);
              } else {
                throw new Error('Export task timed out - file not found');
              }
            }
          } catch (error) {
            console.error('Error polling for export file:', error);
            setError('Failed to export data: ' + error.message);
            setExportStatus('error');
            setTimeout(() => setExportStatus('idle'), 3000);
          }
        };

        pollForExportFile();
      } else {
        throw new Error('Export failed: No file ID or task ID returned');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data: ' + error.message);
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 3000);
    }
  };

  const handleRegenerateData = async () => {
    // Just open the dialog - keep showing the current table until generation
    // actually succeeds, so closing the dialog without generating doesn't
    // leave the user with "No Data Available"
    setShowGenerationDialog(true);
  };

  if (loading) {
    return (
        <div className="text-center py-5">
          <Spinner animation="border"/>
          <p className="mt-2">Loading experiment data...</p>
        </div>
    );
  }

  if (generationStatus === 'generating') {
    return (
        <div className="text-center py-5">
          <Spinner animation="border"/>
          <p className="mt-2">Generating experiment data...</p>
          <small className="text-muted">This may take a few moments</small>
        </div>
    );
  }

  if (adjustmentsStatus === 'applying') {
    return (
        <div className="text-center py-5">
          <Spinner animation="border"/>
          <p className="mt-2">Applying adjustments...</p>
          <small className="text-muted">This may take a few moments</small>
        </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-4">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <div className="mt-2">
          <Button variant="outline-danger" size="sm" onClick={checkExperimentData}>
            Retry
          </Button>
          {/* Show both options when there's an error and no data */}
          {!experimentData && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="ms-2"
                onClick={() => setShowGenerationDialog(true)}
              >
                Generate Data
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                className="ms-2"
                onClick={() => setShowImportDialog(true)}
              >
                Import Data
              </Button>
            </>
          )}
        </div>
      </Alert>
    );
  }

  return (
      <div className="data-analysis-stage">
        {/* Status Messages */}
        {dataError && (
            <Alert variant="danger" className="mb-3" dismissible onClose={() => setDataError(null)}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {dataError}
            </Alert>
        )}

        {adjustmentsError && (
            <Alert variant="danger" className="mb-3" dismissible onClose={() => setAdjustmentsError(null)}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {adjustmentsError}
            </Alert>
        )}

        {adjustmentsSuccess && (
            <Alert variant="success" className="mb-3" dismissible onClose={() => setAdjustmentsSuccess(null)}>
              <i className="bi bi-check-circle me-2"></i>
              {adjustmentsSuccess}
            </Alert>
        )}

        <div className="mb-4">
          <h4>Data Analysis</h4>
          <p className="text-muted">
            Review and edit your data, then perform statistical analysis and machine learning.
          </p>
        </div>

        {/* Range Selection Mode Overlay */}
        {isRangeSelectionMode && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <i className="bi bi-cursor me-2"></i>
                  <strong>Range Selection Mode:</strong> Click and drag to select data range for analysis
                </div>
                <Button variant="outline-secondary" size="sm" onClick={cancelRangeSelection}>
                  Cancel
                </Button>
              </div>
            </Alert>
        )}

        {!experimentData ? (
            <div className="text-center py-5">
              <h5>No Data Available</h5>
              <p className="text-muted mb-4">
                You can either generate data from your images or import data from an Excel/CSV file.
              </p>

              <div className="d-flex justify-content-center gap-3">
                <Button
                    variant="primary"
                    onClick={() => setShowGenerationDialog(true)}
                    className="px-4"
                >
                  <i className="bi bi-magic me-2"></i>
                  Generate Data
                </Button>

                <Button
                    variant="outline-success"
                    onClick={() => setShowImportDialog(true)}
                    className="px-4"
                >
                  <i className="bi bi-upload me-2"></i>
                  Import Data
                </Button>
              </div>
            </div>
        ) : (
            <Row>
              {/* Analysis Tools */}
              <Col lg={3}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Analysis Tools</h6>
                  </Card.Header>
                  <Card.Body className="p-2">
                    <AnalysisToolsContainer
                        onAnalysisSelect={handleAnalysisStart}
                        onExportExcel={handleExportExcel}
                        dataInfo={{
                          rows: experimentData.data_matrix?.length || 0,
                          columns: Object.keys(experimentData.columns_metadata || {}).length,
                          samples: experimentData.data_matrix ?
                              new Set(experimentData.data_matrix.map(row => row.sample_name)).size : 0
                        }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              {/* Data Table */}
              <Col lg={9}>
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      Experiment Data Table
                      <small className="text-muted ms-2">
                        ({experimentData.data_matrix?.length || 0} rows
                        × {Object.keys(experimentData.columns_metadata || {}).length} columns)
                      </small>
                    </h6>

                    <div>
                      {/* Excel Operations Group */}
                      <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => setShowImportDialog(true)}
                          className="me-2"
                      >
                        <i className="bi bi-upload me-1"></i>
                        Import
                      </Button>

                      <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleExportExcel}
                          className="me-2"
                          disabled={exportStatus === 'exporting' || exportStatus === 'queued'}
                      >
                        {exportStatus === 'exporting' || exportStatus === 'queued' ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1"/>
                              {exportStatus === 'queued' ? 'Queued...' : 'Exporting...'}
                            </>
                        ) : (
                            <>
                              <i className="bi bi-download me-1"></i>
                              Export
                            </>
                        )}
                      </Button>

                      {/* Divider / Separator */}
                      <span className="text-muted mx-1">|</span>

                      {/* Regenerate Button */}
                      <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleRegenerateData}
                          className="ms-2"
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Regenerate
                      </Button>
                    </div>
                  </Card.Header>

                  <Card.Body className="p-0">
                    {experimentData && experimentData.data_matrix && experimentData.columns_metadata ? (
                        <DataTable
                            data={experimentData}
                            onCellUpdate={handleCellUpdate}
                            onCellsUpdate={handleCellsUpdate}
                            onRangeSelection={handleRangeSelection}
                            isRangeSelectionMode={isRangeSelectionMode}
                        />
                    ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">No data to display</p>
                        </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
        )}

        {/* Analysis Dialog */}
        <AnalysisDialog
            show={showAnalysisDialog}
            onHide={() => setShowAnalysisDialog(false)}
            analysisType={selectedAnalysisType}
            tableData={experimentData}
            experimentId={experiment?.id}
            onSubmit={handleAnalysisSubmit}
            onStartRangeSelection={startRangeSelection}
        />

        {/* Data Generation Dialog */}
        <DataGenerationDialog
            show={showGenerationDialog}
            onHide={() => setShowGenerationDialog(false)}
            experiment={experiment}
            onGenerate={handleGenerateData}
            onUpdate={handleExperimentUpdate}
        />

        {/* Adjustments Dialog */}
        <AdjustmentsDialog
            show={showAdjustmentsDialog}
            onHide={() => setShowAdjustmentsDialog(false)}
            experiment={experiment}
            adjustmentsTables={adjustmentsTables}
            adjustmentsLoading={adjustmentsLoading}
            onLoadTables={loadAdjustmentsTables}
            onCreateTable={handleCreateAdjustmentsTable}
            onUpdateTable={handleUpdateAdjustmentsTable}
            onDeleteTable={handleDeleteAdjustmentsTable}
            onUploadExcel={handleUploadExcelAdjustments}
            onApplyAdjustments={handleApplyAdjustments}
            adjustmentsError={adjustmentsError}
            adjustmentsSuccess={adjustmentsSuccess}
        />
        <ImportExcelDialog
            show={showImportDialog}
            onHide={() => setShowImportDialog(false)}
            experimentId={experiment.id}
            onImportSuccess={handleImportSuccess}
        />

      </div>

  );
};

export default DataAnalysisStage;